import { InformationIcon } from "@/assets/icons";
import { DatasetTypeIcon } from "@/components/dataset/datasetList/DatasetTypeIcon";
import MapMenuItemControl from "@/components/map/menuItemControl";
import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from "@/components/ui/accordion";
import Checkbox from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import Tooltip from "@/components/ui/tooltip";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import {
  type Dataset,
  DatasetAttributeSchema,
  WeightValueEntry,
  WeightValues
} from "@/types/dataset";
import { ScoringOptions, ScoringOptionsEnum } from "@/types/spatial";
import {
  getIsIncludedScoring,
  getNegativeImpactValue,
  getWeightString
} from "@/utils/weightValues";

import { twMerge } from "tailwind-merge";

import { InputWithClear } from "./InputWithClear";

const WeightPercentage = ({ value = "0" }: { value?: string }) => {
  return (
    <div
      className="max-w-[60px] flex flex-row items-center justify-center h-fit bg-[#EEE] text-txtBody rounded-lg px-2 py-1 text-xs font-semibold border border-gainsboro"
      aria-label="weight-percentage"
    >
      {value}%
    </div>
  );
};

interface DatasetAttributeItemProps {
  dataset: Dataset;
  datasetAttributesWeights: Record<string, WeightValueEntry>;
  datasetWeightValue: string;
  datasetWeightPercentage: string;
  attributeGlobalPercentages: Record<string, string>;
  isAllAttributeWeightZero: boolean;
  isAllDatasetWeightZero: boolean;
  datasetIsIncludedScoring: boolean;
  setLocalWeightValues: React.Dispatch<React.SetStateAction<WeightValues>>;
  selectedScoringOption: ScoringOptions | null;
}

const DatasetAttributeItem = ({
  dataset,
  datasetAttributesWeights,
  datasetWeightValue,
  datasetWeightPercentage,
  attributeGlobalPercentages,
  isAllAttributeWeightZero,
  isAllDatasetWeightZero,
  datasetIsIncludedScoring,
  setLocalWeightValues,
  selectedScoringOption
}: DatasetAttributeItemProps) => {
  const datasetId = dataset.data.id;
  const isPoiDataset = dataset.properties.spatialType === "poi";
  const { isAttributeWeightToggleEnabled } = useAppFeatures();
  const showToggle = isAttributeWeightToggleEnabled;

  const datasetCategories = dataset.categories.reduce<
    Record<string, DatasetAttributeSchema[][number]>
  >((acc, category) => {
    acc[category.id] = category;
    return acc;
  }, {});

  const handleUpdateDatasetWeightValue = (value: string, isPOI?: boolean) => {
    setLocalWeightValues((prevState) => {
      const prevDataset = prevState[datasetId];
      const attributeId = Object.keys(prevDataset.attributes)[0];
      const prevAttr = prevDataset.attributes[attributeId];
      const nextPoiAttr: WeightValueEntry = {
        ...(typeof prevAttr === "object" && prevAttr ? prevAttr : {}),
        weight: value
      };
      return {
        ...prevState,
        [datasetId]: {
          ...prevDataset,
          weight: value,
          attributes: isPOI
            ? { [attributeId]: nextPoiAttr }
            : prevDataset.attributes
        }
      };
    });
  };

  const updateLocalEntry = (
    attributeId: string,
    updater: (prev?: WeightValueEntry) => WeightValueEntry
  ) => {
    setLocalWeightValues((prevState) => {
      const prevDataset = prevState[datasetId] || {
        weight: "",
        attributes: {}
      };

      return {
        ...prevState,
        [datasetId]: {
          ...prevDataset,
          attributes: {
            ...prevDataset.attributes,
            [attributeId]: updater(prevDataset.attributes[attributeId])
          }
        }
      };
    });
  };

  const handleUpdateWeightValue = (categoryIdx: string, value: string) => {
    updateLocalEntry(categoryIdx, (prev) => {
      const base: WeightValueEntry =
        prev && typeof prev === "object" ? { ...prev } : { weight: "0" };
      return { ...base, weight: value };
    });
  };

  const handleUpdateNegativeImpact = (
    attributeId: string,
    checked: boolean
  ) => {
    updateLocalEntry(attributeId, (prev) => {
      const base: WeightValueEntry =
        prev && typeof prev === "object" ? { ...prev } : { weight: "0" };
      const next: WeightValueEntry = { ...base };
      if (checked) {
        next.negative_impact = true;
      } else {
        delete next.negative_impact;
      }
      return next;
    });
  };

  const handleToggleDataset = (checked: boolean) => {
    setLocalWeightValues((prev) => ({
      ...prev,
      [datasetId]: {
        ...prev[datasetId],
        include_scoring: checked
      }
    }));
  };

  const handleToggleAttribute = (attributeId: string, checked: boolean) => {
    setLocalWeightValues((prev) => {
      const prevDataset = prev[datasetId] || { weight: "", attributes: {} };
      const prevAttr = prevDataset.attributes[attributeId];
      const baseAttr: WeightValueEntry =
        prevAttr && typeof prevAttr === "object"
          ? { ...prevAttr }
          : { weight: "0" };
      const nextAttributes = {
        ...prevDataset.attributes,
        [attributeId]: { ...baseAttr, include_scoring: checked }
      };

      // Auto-disable parent when no attribute remains included.
      // Auto-enable parent when toggling any child ON.
      const anyIncluded = Object.values(nextAttributes).some((entry) =>
        getIsIncludedScoring(entry)
      );
      const nextDatasetIncluded = checked ? true : anyIncluded;

      return {
        ...prev,
        [datasetId]: {
          ...prevDataset,
          include_scoring: nextDatasetIncluded,
          attributes: nextAttributes
        }
      };
    });
  };

  const categoriesContent = Object.keys(datasetAttributesWeights).map((id) => {
    const attributeName =
      dataset.properties.spatialType === "poi"
        ? "Total POI Count" // For POI datasets, only show 1 attribute that can be assigned weight called "Total POI Count"
        : datasetCategories[id].attribute_name;
    const attrIncluded =
      datasetIsIncludedScoring &&
      getIsIncludedScoring(datasetAttributesWeights[id]);
    const percentageDisplay = attrIncluded
      ? attributeGlobalPercentages[id].toString().replace(/\.00$/, "")
      : "0";

    return (
      <div key={id}>
        <div className="flex items-center  justify-between gap-1">
          <div
            className={twMerge(
              "text-xs text-txtBody mb-2 line-clamp-2",
              !attrIncluded && "opacity-40"
            )}
            title={attributeName}
          >
            {attributeName}
          </div>
          {showToggle && (
            <Switch
              aria-label={`include-attribute-${datasetId}-${id}`}
              checked={attrIncluded}
              onCheckedChange={(checked) => handleToggleAttribute(id, checked)}
            />
          )}
        </div>

        <div
          className={twMerge("grid items-center gap-3 grid-cols-[1fr_60px]")}
        >
          <div className={twMerge(!attrIncluded && "opacity-40")}>
            <InputWithClear
              inputClassName="text-xs"
              className="bg-white max-h-8 rounded-xl text-txtHeading"
              value={getWeightString(datasetAttributesWeights[id])}
              inputMode="decimal"
              dataTestId={`weight-input-${datasetId}-${id}`}
              onChange={(value) => handleUpdateWeightValue(id, value)}
              placeholder="Enter weight value"
              ariaLabel={`Weight value for ${attributeName}`}
              error={isAllAttributeWeightZero}
              disabled={!attrIncluded}
            />
          </div>
          <div className={twMerge(!attrIncluded && "opacity-40")}>
            <WeightPercentage value={percentageDisplay} />
          </div>
        </div>
        {selectedScoringOption === ScoringOptionsEnum.NORMALIZED && (
          <div className={twMerge(!attrIncluded && "opacity-40")}>
            <Checkbox
              inputClassName="size-4"
              className="mt-1.5 text-xs text-txtBody"
              label="Negative impact"
              onChange={(checked) => handleUpdateNegativeImpact(id, checked)}
              checked={Boolean(
                getNegativeImpactValue(datasetAttributesWeights[id])
              )}
            />
          </div>
        )}
      </div>
    );
  });

  if (isPoiDataset) {
    const poiPercentageDisplay = datasetIsIncludedScoring
      ? datasetWeightPercentage?.toString().replace(/\.00$/, "")
      : "0";

    return (
      <div className="flex flex-col w-full flex-1">
        <div className="flex items-center gap-1 px-4">
          <DatasetTypeIcon type={dataset.data.spatial_aggregation_type} />

          <Tooltip content={dataset.data.display_name} position="top-left">
            <span
              className={twMerge(
                "text-sm text-txtHeading font-semibold line-clamp-2 min-w-0 break-all",
                !datasetIsIncludedScoring && "opacity-40"
              )}
            >
              {dataset.data.display_name}
            </span>
          </Tooltip>

          {showToggle && (
            <Switch
              aria-label={`include-dataset-${datasetId}`}
              checked={datasetIsIncludedScoring}
              onCheckedChange={handleToggleDataset}
              className="ml-auto"
            />
          )}
        </div>

        <div
          className={twMerge(
            "flex-1 grid grid-cols-[1fr_60px] w-full items-center gap-3 px-4 py-2",
            !datasetIsIncludedScoring && "opacity-40"
          )}
        >
          <InputWithClear
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            inputClassName="text-xs"
            className="bg-white max-h-8 rounded-xl text-txtHeading"
            value={datasetWeightValue?.toString()}
            onChange={(value) => handleUpdateDatasetWeightValue(value, true)}
            inputMode="decimal"
            error={isAllDatasetWeightZero}
            disabled={!datasetIsIncludedScoring}
          />
          <WeightPercentage value={poiPercentageDisplay} />
        </div>
        {selectedScoringOption === ScoringOptionsEnum.NORMALIZED && (
          <div className={twMerge(!datasetIsIncludedScoring && "opacity-40")}>
            <Checkbox
              inputClassName="size-4"
              className="mt-1.5 text-xs text-txtBody px-4"
              label="Negative impact"
              onChange={(checked) =>
                handleUpdateNegativeImpact(
                  Object.keys(datasetAttributesWeights)[0],
                  checked
                )
              }
              checked={Boolean(
                getNegativeImpactValue(
                  datasetAttributesWeights[
                    Object.keys(datasetAttributesWeights)[0]
                  ]
                )
              )}
            />
          </div>
        )}
      </div>
    );
  }

  const datasetPercentageDisplay = datasetIsIncludedScoring
    ? datasetWeightPercentage?.toString().replace(/\.00$/, "")
    : "0";

  return (
    <AccordionItem
      value={dataset.data.id}
      className="flex flex-col w-full flex-1"
    >
      <div className="flex flex-row items-center px-4">
        <div className="flex items-center gap-1 mr-auto">
          <DatasetTypeIcon type={dataset.data.spatial_aggregation_type} />
          <Tooltip content={dataset.data.display_name} position="top-left">
            <span
              className={twMerge(
                "text-sm text-txtHeading font-semibold line-clamp-2 min-w-0 break-all",
                !datasetIsIncludedScoring && "opacity-40"
              )}
            >
              {dataset.data.display_name}
            </span>
          </Tooltip>
        </div>
        {showToggle && (
          <Switch
            aria-label={`include-dataset-${datasetId}`}
            checked={datasetIsIncludedScoring}
            onCheckedChange={handleToggleDataset}
            className="mr-2"
          />
        )}

        <MapMenuItemControl
          tooltipContent={
            <div>
              <p className="font-semibold">Negative Impact</p>
              <p>
                Set weight at dataset level to <br /> distribute equally across
                attributes
              </p>
            </div>
          }
          tooltipPosition="top-left"
          className="cursor-pointer w-5 h-5"
        >
          <InformationIcon className="fill-dim-gray" />
        </MapMenuItemControl>
      </div>

      <div className="flex flex-row items-center px-4">
        <div className="flex flex-col gap-3 w-full pt-2 mr-3">
          <div
            className={twMerge(
              "flex-1 grid grid-cols-[1fr_60px] w-full items-center gap-3",
              !datasetIsIncludedScoring && "opacity-40"
            )}
          >
            <InputWithClear
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              inputClassName="text-xs"
              className="bg-white max-h-8 rounded-xl text-txtHeading"
              value={datasetWeightValue?.toString()}
              onChange={(value) => handleUpdateDatasetWeightValue(value)}
              inputMode="decimal"
              error={isAllDatasetWeightZero}
              disabled={!datasetIsIncludedScoring}
            />
            <WeightPercentage value={datasetPercentageDisplay} />
          </div>
        </div>
        <AccordionTrigger className="w-auto px-0" />
      </div>

      <AccordionContent className="flex flex-col py-2 px-3  gap-2 mx-4 bg-white-smoke border border-whisper rounded-xl mt-2">
        {categoriesContent}
      </AccordionContent>
    </AccordionItem>
  );
};

export default DatasetAttributeItem;
